/* Copied from widgets/event-details/src/lib/bottom-dock.ts so the Collection's Who card docks its Next button the way an event's does. Keep the two in step; the one difference is the card it docks over is [data-dock-card] (the Collection's Who card is not a .card). */
'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * `POSITION: STICKY` BY HAND, BECAUSE THE REAL ONE CANNOT WORK HERE.
 *
 * woodsidebible.org runs Salient, and Salient sets `overflow: hidden` on
 * <body>. That makes the body a scroll container — one that never scrolls,
 * because the document scrolls on <html> — so every sticky box inside it is
 * measured against a scrollport permanently at zero and never sticks to
 * anything. It is not this widget: a bare `position: sticky; top: 0` probe
 * appended to the page's own body moves one-for-one with the scroll, and the
 * Who panel's `lg:sticky lg:top-24` has never held either.
 *
 * The cure is one line in the theme and it is the site's call, not the
 * widget's, so the widget carries its own: measure where the bar would sit,
 * and when that place is past the bottom of the window, lift it out of the
 * flow and pin it there. Same behaviour sticky would have given, same result
 * on a page where sticky works.
 *
 * `active` is the ask, not the answer: pass false and nothing ever floats,
 * which is how a button with nothing to do stays out of the way.
 */
export interface Dock {
  left: number;
  width: number;
  height: number;
}

/**
 * The whole decision, with no DOM in it: where the bar belongs, where the
 * card it belongs to is, and how tall the window is.
 *
 * `place` is the bar's own slot, which keeps the bar's height while the bar
 * is pinned. That is what stops the two states fighting — docking cannot move
 * the thing being measured, so there is no position where it flickers.
 *
 * TWO CONDITIONS, AND THE SECOND IS THE ONE HAND-WRITTEN STICKY FORGETS.
 *
 * The first is obvious: the bar's own place is below the bottom of the
 * window, so it needs lifting. The second is the clamp real sticky applies
 * for free — a sticky box may never be positioned outside its containing
 * block. Without it, a phone scrolled back up to the poster still satisfied
 * "my place is below the window" and left the bar riding the bottom of the
 * screen three screens above the card it came from, under the registration
 * dock, with its fade showing against the artwork.
 *
 * So the bar docks only while the whole of it would still land inside the
 * card. Reach the card and it appears; scroll away and it goes home.
 */
export function dockFor(
  place: { bottom: number; height: number },
  card: { top: number; left: number; width: number },
  viewportHeight: number
): Dock | null {
  if (place.bottom <= viewportHeight) return null;
  if (card.top + place.height > viewportHeight) return null;
  return { left: card.left, width: card.width, height: place.height };
}

export function useBottomDock(active: boolean) {
  const slot = useRef<HTMLDivElement | null>(null);
  const [dock, setDock] = useState<Dock | null>(null);

  useEffect(() => {
    if (!active) {
      setDock(null);
      return;
    }
    let frame = 0;

    const measure = () => {
      frame = 0;
      const node = slot.current;
      const bar = node?.firstElementChild as HTMLElement | null;
      if (!node || !bar) return;

      // Pinned over the card it came from, not over the window: on a wide
      // screen the panel is a column, and a full-width bar under it would
      // belong to the page instead of to the card.
      const box = node.getBoundingClientRect();
      const card = (node.closest('[data-dock-card]') as HTMLElement | null) ?? node;
      const next = dockFor(
        { bottom: box.bottom, height: box.height || bar.offsetHeight },
        card.getBoundingClientRect(),
        window.innerHeight
      );
      setDock((was) => {
        if (!next) return was ? null : was;
        return was &&
          was.left === next.left &&
          was.width === next.width &&
          was.height === next.height
          ? was
          : next;
      });
    };

    const later = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', later, { passive: true });
    window.addEventListener('resize', later);
    // A hidden tab runs no animation frames, so a scroll in another tab's
    // window leaves this reading whatever it last saw. Catch up on the way
    // back rather than showing a bar pinned to the wrong place for a moment.
    document.addEventListener('visibilitychange', later);
    // Ticking somebody adds a row above the bar, which moves it without
    // anybody scrolling.
    const card = slot.current?.closest('[data-dock-card]') ?? slot.current;
    const watch = card ? new ResizeObserver(later) : null;
    if (card && watch) watch.observe(card);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', later);
      window.removeEventListener('resize', later);
      document.removeEventListener('visibilitychange', later);
      watch?.disconnect();
    };
  }, [active]);

  return { slot, dock };
}
